import { ellipsisUtil } from 'utils/common';
import { getThumbnailImageFromDiscoverItemCard } from 'utils/discover';
import { DiscoverItemCard, DiscoverItemType, TemplateType } from 'utils/gloo-types';

import Card from 'components/atoms/Card';
import Markdown from 'components/atoms/Markdown';
import { pluralStr } from 'utils';
import Button from 'components/atoms/Button';
import Typography from 'components/atoms/Typography';
import ButtonWithHelp from 'components/atoms/ButtonWithHelp';
import { matchPath, useLocation } from 'react-router';
import InAppRoutes from 'utils/router';
import { Configs } from 'utils/constants';
import { toast } from 'react-toastify';
import Tag from 'components/atoms/Tag';

export interface DiscoverCardProps {
  itemCard: DiscoverItemCard;
  onClick?: (item: DiscoverItemCard) => void;
  className?: string;
}

const CalloutMapping = {
  [TemplateType.KEYWORD]: 'Keyword',
  [TemplateType.BROADCAST]: 'Broadcast',
  [TemplateType.SERIES]: 'Series',
  [TemplateType.LINK]: 'Link',
  [TemplateType.SCHOLARSHIP]: 'Scholarship',
  [TemplateType.DOWNLOAD]: 'Download',
  [TemplateType.SURVEY]: 'Survey',
  [TemplateType.VIDEO]: 'Video',
  [DiscoverItemType.TEMPLATE]: 'Template',
  [DiscoverItemType.KIT]: 'Kit',
};

const ActionMapping = {
  [TemplateType.KEYWORD]: 'See Keyword',
  [TemplateType.BROADCAST]: 'See Broadcast',
  [TemplateType.SERIES]: 'See Series',
  [TemplateType.LINK]: 'See Link',
  [TemplateType.DOWNLOAD]: 'See Download',
  [TemplateType.SCHOLARSHIP]: 'See Scholarship',
  [TemplateType.SURVEY]: 'See Survey',
  [TemplateType.VIDEO]: 'See Video',
  [DiscoverItemType.TEMPLATE]: 'See Template',
  [DiscoverItemType.KIT]: 'Explore Kit',
  [DiscoverItemType.HUB]: 'Explore Hub',
};

const DisplayEngageMapping = {
  [TemplateType.KEYWORD]: true,
  [TemplateType.BROADCAST]: true,
  [TemplateType.SERIES]: true,
  [TemplateType.LINK]: false,
  [TemplateType.DOWNLOAD]: false,
  [TemplateType.SCHOLARSHIP]: false,
  [TemplateType.SURVEY]: true,
  [TemplateType.VIDEO]: false,
  [DiscoverItemType.TEMPLATE]: false,
  [DiscoverItemType.KIT]: false,
  [DiscoverItemType.HUB]: false,
};

function DiscoverCardPreview({ itemCard: item, onClick, className }: DiscoverCardProps) {
  const location = useLocation();

  const displayEngageTag: boolean =
    (DisplayEngageMapping[item.templateType || item.discoverType] || item.templateType || item.discoverType) === true;
  const actionText: string =
    ActionMapping[item.templateType || item.discoverType] || item.templateType || item.discoverType;

  const isOnAuthoringPage = !!matchPath(location.pathname, { path: ['/admin/'] });

  return (
    <div className={className || 'w-full px-2 pb-8 md:w-1/2 lg:w-1/3 xl:w-1/4 2xl:w-1/5'}>
      <Card
        border
        className="h-full overflow-hidden shadow-md flex flex-col cursor-pointer p-0 hover:bg-neutral-100"
        onClick={onClick && (() => onClick(item))}
      >
        <div className="flex flex-col h-full justify-between">
          <div className="h-full flex flex-col">
            <div className="relative aspect-video">
              <div className="overflow-hidden absolute w-full h-full">
                <img
                  alt={item.title}
                  className="object-cover object-bottom w-full h-full bg-white"
                  src={getThumbnailImageFromDiscoverItemCard(item)}
                />
              </div>
            </div>
            <div className="py-6 px-6 h-full flex-1 flex flex-col items-start">
              <div className="flex-1 w-full">
                <div className="flex items-center">
                  <Tag
                    text={(
                      CalloutMapping[item.templateType || item.discoverType] ||
                      item.templateType ||
                      item.discoverType
                    ).toUpperCase()}
                    size="xs"
                    color="gray"
                  />
                  {!isOnAuthoringPage && (
                    <>
                      <div className="flex-1" />
                      <ButtonWithHelp
                        type="icon-only"
                        dataDurableId="copyDirectLinkToDiscoverItem"
                        helpText="Admin - Copy direct link to this item"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          let link = '';
                          if (item.discoverType === DiscoverItemType.HUB) {
                            link = InAppRoutes.getString(InAppRoutes.DiscoverHub, item.itemId);
                          } else if (item.discoverType === DiscoverItemType.KIT) {
                            link = InAppRoutes.getString(InAppRoutes.DiscoverKit, item.itemId);
                          } else if (item.templateType) {
                            link = InAppRoutes.getString(InAppRoutes.DiscoverItem, item.itemId);
                            const isHubOrKitPage = matchPath(location.pathname, {
                              path: [InAppRoutes.DiscoverHub, InAppRoutes.DiscoverKit],
                            });
                            if (isHubOrKitPage) {
                              const baseHubOrKitPage = location.pathname.split('/').slice(0, 4).join('/');
                              link = `${baseHubOrKitPage}/item/${item.itemId}`;
                            }
                          }

                          if (link) {
                            const fullUrl = Configs.Domain.BaseUrl.replace(/\/$/, '') + link;
                            navigator.clipboard.writeText(fullUrl);
                            toast.success(`Copied to clipboard:\n${fullUrl}`);
                          }
                        }}
                        icon="copy"
                      />
                    </>
                  )}
                </div>
                <Typography variant="xs" className="flex items-center pt-3">
                  {item?.metadata?.author || 'Gloo'}
                  {item?.metadata?.weeks && (
                    <>
                      <span className="inline-block w-1 h-1 mx-1 ml-2 mr-2 bg-charcoal-900 rounded-full" />
                      <Typography variant="xs">
                        {item.metadata.weeks} {pluralStr(item.metadata.weeks, 'Week', 'Weeks')}
                      </Typography>
                    </>
                  )}
                </Typography>
                <Typography variant="h2" className="pb-2 pt-3">
                  {ellipsisUtil(item.title, 60)}
                </Typography>
                <Typography variant="base" className="font-normal pb-3">
                  <Markdown defaultStyles={false} content={ellipsisUtil(item.shortDescription, 70)} />
                </Typography>
              </div>
              <Button
                dataDurableId="discoverCardClick"
                size="small"
                className="text-sm mt-6"
                icon={displayEngageTag ? 'message-square' : 'arrow-right'}
                iconPosition="right"
                type="primary"
                text={actionText}
                onClick={onClick && (() => onClick(item))}
              />
            </div>
          </div>
        </div>
        <div>
          {item.templateType === TemplateType.SCHOLARSHIP && (
            <div className="bg-neutral-100 py-3 px-5 flex items-center min-h-[48px]">
              <div className="flex justify-center relative items-center flex-row">
                <Typography variant="base" className="text-xs line-through">
                  {item.scholarshipData?.originalCost}
                </Typography>
                <Typography variant="base" className="text-xs ml-2">
                  {item.scholarshipData?.newCost} w/ scholarship
                </Typography>
              </div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}

export default DiscoverCardPreview;
